// Reading the capability off a URL (threads §5.4, §20.10).
//
// Two halves of the address bar, and which half a thing goes in is a
// security property rather than a style choice:
//
//   - The **fragment** carries the capability. Browsers never transmit
//     it, so the page host — the operator, a CDN, an app-store landing
//     page — never receives a tracking key. There is no endpoint anywhere
//     that sees one.
//   - The **query** carries the optional `?b=` bootstrap hint: one to
//     three multiaddrs a cold device can dial to reach the mesh at all.
//     That is a *location*, not a capability, and a location is exactly
//     the kind of thing a host may see.
//
// The decoders live in the engine bundle; this file is the rule about
// which half is read for what, plus the states a page can be in before it
// has a run to show.

import {
  RECIPIENT_SEPARATOR,
  decodeRecipientStop,
  encodeRecipientStop,
  type RecipientStop
} from "./recipient.js";

/** What the address bar amounts to, before anything is opened. */
export type CapabilityState =
  /** No fragment at all — somebody opened the bare page. */
  | { kind: "absent" }
  /** A fragment that is not a link: a truncated paste, a mangled SMS. */
  | { kind: "unreadable"; reason: string }
  /** A link, decoded. Whether it can be *followed* is a separate question. */
  | {
    kind: "present";
    fragment: string;
    bootstrap: string[];
    /**
     * The recipient's own stop, when their copy of the link carries one
     * (see recipient.ts). Null for a plain link, which still follows the
     * run perfectly — it just cannot say when the van reaches *this* door.
     */
    recipient: RecipientStop | null;
  };

/** The fragment, whitespace stripped — a link hard-wrapped by a mail client is not broken. */
export function fragmentOf(url: string): string {
  const text = String(url ?? "").trim();
  const hash = text.indexOf("#");
  return (hash < 0 ? "" : text.slice(hash + 1)).replace(/\s+/gu, "");
}

/**
 * Reads the address bar.
 *
 * `parseBootstrap` is the engine's own `parseBootstrapHint`, injected so
 * this file stays free of the bundle. It is deliberately lenient: the
 * capability is in the fragment and must keep working when the query is
 * garbage, truncated by a messaging app, or carrying somebody else's
 * tracking parameters.
 */
export function readCapability(
  url: string,
  parseBootstrap: (url: string) => string[] = () => []
): CapabilityState {
  const whole = fragmentOf(url);
  if (!whole) return { kind: "absent" };
  // The capability comes first and a recipient card, if any, after the
  // separator. Split before validating, so a card never makes an
  // otherwise-good capability look truncated.
  const cut = whole.indexOf(RECIPIENT_SEPARATOR);
  const fragment = cut < 0 ? whole : whole.slice(0, cut);
  const card = cut < 0 ? "" : whole.slice(cut + 1);
  if (!/^[A-Za-z0-9_-]+$/u.test(fragment)) {
    return { kind: "unreadable", reason: "That link is not complete — it may have been cut short." };
  }
  let bootstrap: string[] = [];
  try {
    bootstrap = parseBootstrap(url) ?? [];
  } catch {
    bootstrap = [];
  }
  // An unreadable card is not an unreadable link. The run still follows;
  // only "when does it reach *my* door" is lost, and saying nothing is
  // better than refusing a capability that works.
  const recipient = card ? decodeRecipientStop(card) : null;
  return { kind: "present", fragment, bootstrap, recipient };
}

/** Builds one recipient's copy of a tracking URL from the run's own link. */
export function recipientUrl(trackUrl: string, stop: RecipientStop): string {
  const base = String(trackUrl ?? "");
  const hash = base.indexOf("#");
  if (hash < 0) return base;
  const capability = fragmentOf(base);
  const cut = capability.indexOf(RECIPIENT_SEPARATOR);
  const bare = cut < 0 ? capability : capability.slice(0, cut);
  return `${base.slice(0, hash)}#${bare}${RECIPIENT_SEPARATOR}${encodeRecipientStop(stop)}`;
}

/**
 * The capability must never travel in the query, and this is the assert
 * that says so. Called on every URL the app builds, in dev and in prod:
 * a regression here silently hands a tracking key to a CDN log.
 */
export function assertCapabilityNotInQuery(url: string): void {
  const text = String(url ?? "");
  const hash = text.indexOf("#");
  const head = hash < 0 ? text : text.slice(0, hash);
  const question = head.indexOf("?");
  if (question < 0) return;
  for (const pair of head.slice(question + 1).split("&")) {
    const [key, value = ""] = pair.split("=");
    if (key === "b") continue; // the bootstrap hint is a location, not a key
    // A 78-byte link is 104 base64url characters. Anything close to that
    // long in a query string is a capability that has escaped its fragment.
    if (/^[A-Za-z0-9_-]{40,}$/u.test(decodeURIComponent(value))) {
      throw new Error(
        `A capability must never travel in the query string: "${key}" looks like one. `
        + "The fragment is what browsers do not transmit (threads §5.4)."
      );
    }
  }
}
